import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getPostpartumMedsTherapiesRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";
import { usDateTimeFormat } from "@/utils/datetime";

interface NewbornMedsAndTherapyRowProps {
  time: string;
  observation: Observation;
  isViewOnly: boolean;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
}

export default function NewbornMedsAndTherapyRow({
  time,
  observation,
  isViewOnly,
  showNumbersOnly,
  showPreviousVersions = true,
  onRefresh,
}: NewbornMedsAndTherapyRowProps) {
  const { medications, therapies, ivAccess, fluidVolume, notes, hasAnswers, hasNotes } = useMemo(
    () => getPostpartumMedsTherapiesRowValues(observation),
    [observation],
  );

  const hasMedications = medications && medications.some((med) => med.medication);
  const hasTherapies = therapies && therapies.some((therapy) => therapy.therapy);

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Meds & Therapies:</TextWrapper>
            <List fz="sm" withPadding>
              {hasMedications && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Medication:
                  </TextWrapper>
                  <List withPadding type="ordered">
                    {medications?.map((med, index) =>
                      med.medication ? (
                        <List.Item key={`med-${index}`}>
                          <TextWrapper size="sm" variant="bold">
                            {med.medication.charAt(0).toUpperCase() + med.medication.slice(1)}:
                          </TextWrapper>
                          <List withPadding>
                            {med.form && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Form -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.form}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.dosage && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Dosage -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.dosage}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.frequency && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Frequency -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.frequency}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.duration && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Duration -
                                  </TextWrapper>
                                  <TextWrapper size="sm">
                                    {med.duration === "X days" && med.xDays
                                      ? `${med.xDays} Days`
                                      : med.duration}
                                  </TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.prn && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    PRN -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.prn}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                            {med.specialInstructions && (
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Special Instructions -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{med.specialInstructions}</TextWrapper>
                                </Group>
                              </List.Item>
                            )}
                          </List>
                        </List.Item>
                      ) : null,
                    )}
                  </List>
                </List.Item>
              )}
              {hasTherapies && (
                <List.Item>
                  <TextWrapper size="sm" variant="bold">
                    Therapy:
                  </TextWrapper>
                  <List withPadding type="ordered">
                    {therapies?.map((therapy, index) =>
                      therapy.therapy ? (
                        <List.Item key={`therapy-${index}`}>
                          <TextWrapper size="sm" variant="bold">
                            {therapy.therapy.charAt(0).toUpperCase() + therapy.therapy.slice(1)}:
                          </TextWrapper>
                          {therapy.reason && (
                            <List withPadding>
                              <List.Item>
                                <Group gap="xs">
                                  <TextWrapper size="sm" variant="bold">
                                    Reason -
                                  </TextWrapper>
                                  <TextWrapper size="sm">{therapy.reason}</TextWrapper>
                                </Group>
                              </List.Item>
                            </List>
                          )}
                        </List.Item>
                      ) : null,
                    )}
                  </List>
                </List.Item>
              )}
              {ivAccess && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      IV Access:
                    </TextWrapper>
                    <TextWrapper size="sm">{usDateTimeFormat(ivAccess)}</TextWrapper>
                  </Group>
                </List.Item>
              )}
              {fluidVolume && (
                <List.Item>
                  <Group gap="xs">
                    <TextWrapper size="sm" variant="bold">
                      Fluid Volume:
                    </TextWrapper>
                    <TextWrapper size="sm">{fluidVolume} mL</TextWrapper>
                  </Group>
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Meds & Therapies Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={NewbornMedsAndTherapyRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
